// Sneak peek: the first product in each product grid previews its next image once,
// when it first scrolls into view, then behaves like every other card (a normal
// slider with arrows). No other card peeks, and hovering never plays it.
//
// Driven through the real Swiper instance on the card's carousel
// (<swiper-container>/<swiper-slide>, swiper7.4.1.min.js, rendered by
// snippets/component-product-card.liquid) - not CSS. That carousel slides via
// Swiper's own transform/loop machinery, so the only reliable way to show part of
// the next slide and spring back is through the Swiper API (setTranslate/setTransition).
//
// body.ab-sneak-peek gates whether any of this runs (hardcoded in layout/theme.liquid).

const SWIPER_READY_RETRY_MS = 50;
const SWIPER_READY_MAX_ATTEMPTS = 40; // ~2s, then give up quietly.

const NUDGE_PEEK_RATIO = 0.3; // how far toward the second slide the peek travels.
const NUDGE_OUT_MS = 500;
const NUDGE_HOLD_MS = 400; // how long it sits peeked before springing back.
const NUDGE_BACK_MS = 500;

const PEEK_DELAY_MS = 300; // gives the card's image a moment to paint first.
const PEEK_VISIBLE_RATIO = 0.99; // how much of the card must be on screen to play.

// Shoppers who ask for reduced motion get no peek; the card is a normal slider.
const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

// swiper-container initializes itself asynchronously - .swiper isn't set until the
// underlying Swiper instance exists.
function onSwiperReady(swiperEl, callback) {
  if (swiperEl.swiper) {
    callback(swiperEl.swiper);
    return;
  }

  let attempts = 0;
  const interval = setInterval(() => {
    attempts += 1;
    if (swiperEl.swiper) {
      clearInterval(interval);
      callback(swiperEl.swiper);
    } else if (attempts >= SWIPER_READY_MAX_ATTEMPTS) {
      clearInterval(interval);
    }
  }, SWIPER_READY_RETRY_MS);
}

// swiper7.4.1.min.css hardcodes .swiper-wrapper's transition-timing-function to
// ease-out, which makes the return leg snap back abruptly. Injecting an override
// into the shadow root swaps it for ease-in-out so both legs ease smoothly.
const NUDGE_EASING_STYLE = `
  .swiper-wrapper { transition-timing-function: ease-in-out !important; }
`;

function ensureNudgeEasing(swiperEl) {
  const root = swiperEl.shadowRoot;
  if (!root || root.querySelector('style[data-sneak-peek-easing]')) return;

  const style = document.createElement('style');
  style.setAttribute('data-sneak-peek-easing', '');
  style.textContent = NUDGE_EASING_STYLE;
  root.appendChild(style);
}

// Nudges the wrapper's own translate a fraction of a slide toward the next one
// and springs it back - a preview, not a real slide change. Manipulating
// translate/transition directly (instead of slideTo) keeps activeIndex, slide
// classes and pagination untouched, so nothing is left in a changed state.
function nudgeNext(swiperEl, swiper) {
  if (swiper.destroyed || swiper.sneakPeekNudging) return;
  swiper.sneakPeekNudging = true;

  ensureNudgeEasing(swiperEl);
  // component-product-card.css hides every slide but the active one; the peek
  // needs the next slide visible while it slides in.
  swiperEl.classList.add('is-sneak-peeking');

  // swiper-container is rendered with observer/observe-parents="true" plus
  // Swiper's default resizeObserver, so any layout shift during the peek makes
  // Swiper snap back to its resting position and cut the peek short.
  // __preventObserver__ mutes the Observer module, and swapping
  // setTranslate/setTransition/update for no-ops (driving the animation through
  // the saved originals) blocks the resizeObserver path too.
  swiper.__preventObserver__ = true;
  const originalUpdate = swiper.update.bind(swiper);
  const originalSetTranslate = swiper.setTranslate.bind(swiper);
  const originalSetTransition = swiper.setTransition.bind(swiper);
  swiper.update = () => {};
  swiper.setTranslate = () => {};
  swiper.setTransition = () => {};

  const restTranslate = swiper.translate;
  const peekDistance = swiper.width * NUDGE_PEEK_RATIO;
  const peekedTranslate = swiper.rtlTranslate ? restTranslate + peekDistance : restTranslate - peekDistance;

  let timer;

  function finish() {
    activePeeks.delete(swiperEl);
    swiper.sneakPeekNudging = false;
    swiper.update = originalUpdate;
    swiper.setTranslate = originalSetTranslate;
    swiper.setTransition = originalSetTransition;
    swiper.__preventObserver__ = false;
    swiperEl.classList.remove('is-sneak-peeking');
    unprimeCard(swiperEl);
  }

  // Ends the peek mid-animation: jumps straight back to rest with no transition.
  activePeeks.set(swiperEl, () => {
    clearTimeout(timer);
    if (!swiper.destroyed) {
      originalSetTransition(0);
      originalSetTranslate(restTranslate);
    }
    finish();
  });

  originalSetTransition(NUDGE_OUT_MS);
  originalSetTranslate(peekedTranslate);

  timer = setTimeout(() => {
    if (swiper.destroyed) return;
    originalSetTransition(NUDGE_BACK_MS);
    originalSetTranslate(restTranslate);
    timer = setTimeout(finish, NUDGE_BACK_MS);
  }, NUDGE_OUT_MS + NUDGE_HOLD_MS);
}

// Peeks waiting out PEEK_DELAY_MS. Cleared if the shopper turns on reduced motion
// in the meantime; the preference is also re-read right before each peek plays.
const pendingPeeks = new Set();

// Peeks mid-animation, each mapped to the function that stops it. Turning on reduced
// motion stops them at once rather than letting them play out.
const activePeeks = new Map();

reducedMotion.addEventListener('change', () => {
  if (!reducedMotion.matches) return;
  for (const timer of pendingPeeks) clearTimeout(timer);
  pendingPeeks.clear();
  for (const stopPeek of [...activePeeks.values()]) stopPeek();
  for (const swiperEl of document.querySelectorAll('swiper-container.is-sneak-peek-card')) unprimeCard(swiperEl);
});

// The peek must never slide in an image that isn't ready to draw. Safari only fetches
// and decodes an image once it's about to be painted, and the next slide is hidden
// (opacity 0, clipped outside the card), so it would show white until Safari caught up.
// decode() resolves once the image is downloaded and decoded; if that takes longer than
// IMAGE_READY_TIMEOUT_MS the peek is skipped rather than played over a blank slide.
const IMAGE_READY_TIMEOUT_MS = 4000;

function nextImageReady(swiper) {
  const img = swiper.slides[swiper.activeIndex + 1]?.querySelector('img');
  if (!img) return Promise.resolve(false);
  img.loading = 'eager';
  const ready = img.decode().then(
    () => true,
    () => false
  );
  const timeout = new Promise((resolve) => setTimeout(() => resolve(false), IMAGE_READY_TIMEOUT_MS));
  return Promise.race([ready, timeout]);
}

// From page load until its peek is over, the first card keeps its next slide visible
// (sneak-peek.liquid), still clipped outside the card. Safari doesn't draw a slide that
// turns visible at the same moment it starts sliding in, and showed white there instead.
function unprimeCard(swiperEl) {
  swiperEl.classList.remove('is-sneak-peek-card');
}

// The visibility check watches the card's image frame, not the swiper: on mobile the
// swiper is a few px taller than the frame that clips it, so at most ~98% of it is
// ever visible and a 0.99 threshold on it never fires.
const swiperByFrame = new WeakMap();

// Plays the peek once, the first time enough of the card is on screen. Grids that
// start below the fold wait until the shopper scrolls to them.
const peekObserver = new IntersectionObserver(
  (entries) => {
    for (const entry of entries) {
      if (!entry.isIntersecting) continue;
      const swiperEl = swiperByFrame.get(entry.target);
      peekObserver.unobserve(entry.target);
      if (!swiperEl) continue;
      if (reducedMotion.matches) {
        unprimeCard(swiperEl);
        continue;
      }
      onSwiperReady(swiperEl, async (swiper) => {
        const ready = await nextImageReady(swiper);
        if (!ready) {
          unprimeCard(swiperEl);
          return;
        }
        const timer = setTimeout(() => {
          pendingPeeks.delete(timer);
          if (reducedMotion.matches) unprimeCard(swiperEl);
          else nudgeNext(swiperEl, swiper);
        }, PEEK_DELAY_MS);
        pendingPeeks.add(timer);
      });
    }
  },
  { threshold: PEEK_VISIBLE_RATIO }
);

// The first card in each grid. A filter/sort re-render puts a new element here, so
// it gets its own peek; the same element never peeks twice.
function watchFirstCards() {
  for (const gridContainer of document.querySelectorAll('.product-grid-container')) {
    const firstSwiperEl = gridContainer.querySelector('.product-grid swiper-container');
    if (!firstSwiperEl || firstSwiperEl.dataset.sneakPeekBound) continue;
    firstSwiperEl.dataset.sneakPeekBound = 'true';
    firstSwiperEl.classList.add('is-sneak-peek-card');
    const frameEl = firstSwiperEl.closest('.card-media-child') || firstSwiperEl;
    swiperByFrame.set(frameEl, firstSwiperEl);
    peekObserver.observe(frameEl);
    // Start decoding the image the peek reveals now, so it's usually ready by the time
    // the card scrolls into view.
    onSwiperReady(firstSwiperEl, nextImageReady);
  }
}

let sneakPeekInitialized = false;

function initSneakPeek() {
  if (sneakPeekInitialized) return;
  if (!document.body.classList.contains('ab-sneak-peek')) return;

  sneakPeekInitialized = true;
  bodyClassObserver.disconnect();
  watchFirstCards();

  // The grid gets re-fetched via AJAX on filter/sort changes.
  document.addEventListener('malbon:collection-updated', watchFirstCards);
}

const bodyClassObserver = new MutationObserver(initSneakPeek);
bodyClassObserver.observe(document.body, { attributes: true, attributeFilter: ['class'] });

initSneakPeek();
